import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useNavigate } from "react-router-dom";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import FilePicker from "components/FilePicker";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import { bulkUploadPacks, downloadPackTemplate } from "services/packServices";

const ACCENT = "#0891b2";

interface BulkError {
  row?: number;
  sheet?: string;
  packGroup?: string;
  field?: string;
  message: string;
}

interface BulkResult {
  success: boolean;
  created: number;
  failed: number;
  createdPackIds?: string[];
  errors?: BulkError[];
}

const SectionCard = ({
  step,
  title,
  subtitle,
  isComplete,
  children,
}: {
  step: number | string;
  title: string;
  subtitle?: string;
  isComplete?: boolean;
  children: React.ReactNode;
}) => (
  <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 3, overflow: "hidden" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        borderLeft: `4px solid ${ACCENT}`,
        background: `linear-gradient(135deg, ${ACCENT}0a 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {step}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {isComplete !== undefined && isComplete && <CheckCircleIcon fontSize="small" sx={{ color: "#4caf50" }} />}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

const BulkUploadPacks = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);

  const onDownloadTemplate = async () => {
    try {
      setLoading(true);
      await downloadPackTemplate();
    } catch (error: any) {
      showSnackbar(error.message || "Failed to download template", "error");
    } finally {
      setLoading(false);
    }
  };

  const onUpload = async () => {
    if (!file) return;
    try {
      setLoading(true);
      setResult(null);
      const resp = await bulkUploadPacks(file);
      const data: BulkResult = resp?.data;
      setResult(data);

      if (data?.success) {
        showSnackbar(`Successfully created ${data.created} pack(s).`, "success");
        setFile(null);
      } else {
        showSnackbar(
          `Upload failed validation — ${data?.errors?.length ?? 0} issue(s) found. See the failure log below.`,
          "error",
        );
      }
    } catch (error: any) {
      showSnackbar(error.message || "Bulk upload failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentShell title="Bulk Upload Packs" loading={loading}>
      <Box sx={{ maxWidth: 920, mx: "auto", py: 1 }}>
        {/* Step 1 — template */}
        <SectionCard step={1} title="Download the template">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each pack has one main row (e.g. "Pack_1") for the pack-level fields, followed by its slot
            rows ("Pack_1_Slot_1", "Pack_1_Slot_2", …). Translations go in the per-language columns at
            the end of the pack row. Enum columns are Excel dropdowns. Reference an uploaded image by its
            ID from the Image Repository page.
          </Typography>
          <CustomButton startIcon={<ArrowDownwardIcon />} onClick={onDownloadTemplate}>
            Download Packs Template
          </CustomButton>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2 }}>
            <DescriptionOutlinedIcon fontSize="small" sx={{ color: "text.disabled", mt: "2px" }} />
            <Typography variant="caption" color="text.secondary">
              The template ships with example rows and an Instructions sheet that lists every field,
              whether it is required, and the accepted values.
            </Typography>
          </Box>
        </SectionCard>

        {/* Step 2 — upload */}
        <SectionCard
          step={2}
          title="Upload your completed file"
          subtitle="The whole file is validated before anything is created"
          isComplete={Boolean(file)}
        >
          <FilePicker
            label="Select Packs XLSX"
            name="bulkPackFile"
            fileType=".xlsx, .xls"
            value={file}
            showPreview={false}
            onFileSelect={(f: any) => {
              setFile(f || null);
              setResult(null);
            }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            All-or-nothing: if any row is invalid, <b>nothing</b> is created and you get an exact
            row-and-field failure log below.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <CustomButton startIcon={<CloudUploadIcon />} disabled={!file} onClick={onUpload}>
              Upload &amp; Create
            </CustomButton>
          </Box>
        </SectionCard>

        {/* Results */}
        {result &&
          (result.success ? (
            <Alert icon={<CheckCircleIcon />} severity="success" sx={{ borderRadius: 3, alignItems: "center" }}>
              <AlertTitle sx={{ fontWeight: 700 }}>Created {result.created} pack(s)</AlertTitle>
              They now appear in the Packs list.
              <Box sx={{ mt: 1.5 }}>
                <CustomButton onClick={() => navigate(configData.routes.packs.packs)}>View Packs</CustomButton>
              </Box>
            </Alert>
          ) : (
            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "#f4433655", borderRadius: 3, overflow: "hidden" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  py: 2,
                  borderLeft: "4px solid #f44336",
                  borderBottom: "1px solid",
                  borderBottomColor: "divider",
                  background: "linear-gradient(135deg, #f4433610 0%, transparent 100%)",
                }}
              >
                <ErrorOutlineIcon sx={{ color: "#f44336" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Upload failed — nothing was created
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Fix the rows below and upload again.
                  </Typography>
                </Box>
                <Chip size="small" color="error" label={`${result?.errors?.length ?? 0} issue(s)`} sx={{ fontWeight: 600 }} />
              </Box>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["Row", "Sheet", "Pack", "Field", "Problem"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, background: "#fafafa" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(result.errors ?? []).map((err, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{err.row ?? "—"}</TableCell>
                        <TableCell>{err.sheet ?? "—"}</TableCell>
                        <TableCell>{err.packGroup ?? "—"}</TableCell>
                        <TableCell>
                          {err.field ? <Chip size="small" variant="outlined" label={err.field} /> : "—"}
                        </TableCell>
                        <TableCell sx={{ color: "error.main" }}>{err.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
      </Box>
    </ContentShell>
  );
};

export default BulkUploadPacks;
